import { useEffect, useState } from 'react';
import { Button, Text } from 'components';
import { ThirdPartyLoginButtonProps } from './types';
import { GoogleIcon } from 'assets/svgs';

declare global {
  interface Window {
    google: any;
  }
}

// 인앱 브라우저 감지
const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    ua.includes('kakaotalk') ||
    (ua.includes('naver') && ua.includes('inapp')) ||
    ua.includes('instagram') ||
    ua.includes('fbav')
  );
};

// iOS 감지
const isIOS = (): boolean => {
  return /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
};

// Android 감지
const isAndroid = (): boolean => {
  return /android/.test(navigator.userAgent.toLowerCase());
};

const ThirdPartyLoginButtonSet = ({
  loginType = 'LogIn',
  onClickGoogleButton,
}: ThirdPartyLoginButtonProps) => {
  const [inApp, setInApp] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const [isAndroidDevice, setIsAndroidDevice] = useState(false);

  useEffect(() => {
    // setInApp(true);
    // setIsIOSDevice(false); 
    // setIsAndroidDevice(true);
    setInApp(isInAppBrowser());
    setIsIOSDevice(isIOS());
    setIsAndroidDevice(isAndroid());

    if (!window.google || !window.google.accounts?.oauth2) {
      console.error('Google Identity Services SDK not loaded.');
    }
  }, []);

  const handleGoogleLogin = () => {
    if (inApp) {
      alert('현재 브라우저에서는 Google 로그인이 지원되지 않습니다.\n외부 브라우저(Chrome/Safari 등)로 다시 열어주세요.');
      return;
    }

    const redirectUri = window.location.origin;

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: '387633422555-7rm8ms8a3fo0kuni7ne7p0ancq5u531s.apps.googleusercontent.com',
      scope: 'openid email profile',
      ux_mode: 'popup',
      redirect_uri: redirectUri,
      callback: (response: { code: string }) => {
        if (response.code) {
          onClickGoogleButton(response.code, redirectUri);
        } else {
          console.error('Google login failed', response);
        }
      },
    });

    client.requestCode();
  };

  const buttonClassNames = 'flex h-10 w-full flex-row items-center justify-center gap-2';
  const hrClassNames = 'flex-1 border border-border-primary';

  const copyLinkToClipboard = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      alert('링크가 복사되었습니다. 외부 브라우저(Chrome/Safari)에 붙여넣어 접속해주세요.');
    });
  };

  const openInChrome = () => {
    const currentUrl = window.location.href.replace(/^https?:\/\//, '');
    const intentUrl = `intent://${currentUrl}#Intent;scheme=https;package=com.android.chrome;end;`;
    window.location.href = intentUrl;
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <Button
        icon={<GoogleIcon />}
        className={buttonClassNames}
        text={`${loginType} with Google`}
        type="secondary"
        onClick={handleGoogleLogin}
        disabled={false}
      />

      {inApp && (
        <div className="mt-1 w-full bg-yellow-50 border border-yellow-300 text-sm text-yellow-800 px-4 py-2 rounded">
          <p className="mb-1">
            현재 브라우저(카카오톡, 네이버 등)에서는 Google 로그인이 정상적으로 작동하지 않습니다.
            <br />
            <strong>외부 브라우저에서 이 페이지를 열어주세요.</strong>
          </p>
          <p className="mb-1">
            Google login may not work properly in your current browser (e.g., KakaoTalk, Naver).
            <br />
            <strong>Please open this page in an external browser.</strong>
          </p>

          {isIOSDevice ? (
            <div className="mt-1 flex flex-col gap-1">
              <Text size="body2" color="secondary">
                iOS에서는 링크를 복사하여 Safari에 붙여넣어 주세요.
              </Text>
              <Text size="body2" color="secondary">
                On iOS, please copy the link and paste it into Safari.
              </Text>
              <button
                onClick={copyLinkToClipboard}
                className="text-blue-600 underline text-left"
              >
                Copy Link
              </button>
            </div>
          ) : isAndroidDevice ? (
            <button
              className="underline text-blue-600"
              onClick={openInChrome}
            >
              Chrome으로 열기
            </button>
          ) : (
            <button
              className="underline text-blue-600"
              onClick={() => window.open(window.location.href, '_blank')}
            >
              외부 브라우저에서 열기
            </button>
          )}
        </div>
      )}

      <div className="my-[13.5px] flex w-full flex-row items-center justify-center">
        <hr className={hrClassNames} />
        <Text className="w-10 bg-white text-center" size="body2" color="primary" weight="regular">
          Or
        </Text>
        <hr className={hrClassNames} />
      </div>
    </div>
  );
};

export default ThirdPartyLoginButtonSet;